import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Phone } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { api, errMsg, fcfa, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const METHODS = { wave: "Wave", orange_money: "Orange Money" };

export const localAmount = (price, local) => {
  const f = 10 ** (local.decimals || 0);
  return Math.ceil((price * local.rate.per_1000_xof) / 1000 * f) / f;
};

export const LocalEquivalent = ({ price, local }) => {
  if (!local?.rate) return null;
  return (
    <p data-testid="local-equivalent" className="text-xs text-gray-600">
      ≈ {new Intl.NumberFormat("fr-FR", { maximumFractionDigits: local.decimals }).format(localAmount(price, local))} {local.label} à titre indicatif (taux du {fmtDate(local.rate.rate_date)}, arrondi supérieur). <b>Seul le montant en FCFA est payable.</b>
    </p>
  );
};

export const PaymentForm = ({ plan, local, onDone, onCancel }) => {
  const { data: contact } = useApi("/subscriptions/contact");
  const [method, setMethod] = useState("wave");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/seller/subscriptions", { plan: plan.code, payment_method: method, payment_reference: ref });
      toast.success("Demande envoyée. Votre paiement sera vérifié manuellement.");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="payment-form" className="space-y-4 rounded-2xl border-2 border-orange-200 bg-orange-50 p-5 text-sm">
      <p className="font-display text-lg font-bold">Payer la formule {plan.name}</p>
      <p data-testid="payment-amount" className="font-display text-3xl font-black">{fcfa(plan.price)} <span className="text-base font-semibold text-gray-600">pour {plan.days} jours</span></p>
      <LocalEquivalent price={plan.price} local={local} />
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {Object.entries(METHODS).map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={method === k} onClick={() => setMethod(k)} data-testid={`payment-method-${k}`}
            className={`rounded-full px-4 py-2 font-semibold ring-1 transition-colors ${method === k ? "bg-gray-950 text-white ring-gray-950" : "bg-white ring-gray-300 hover:bg-gray-100"}`}>{l}</button>
        ))}
      </div>
      <ol className="list-decimal space-y-1 pl-5" data-testid="payment-instructions">
        <li>Ouvrez votre application {METHODS[method]}.</li>
        <li>Envoyez exactement <b>{fcfa(plan.price)}</b> au numéro <a href={`tel:${(contact?.phone || "").replace(/\s/g, "")}`} data-testid="payment-phone" className="inline-flex items-center gap-1 font-bold underline"><Phone className="h-3 w-3" />{contact?.phone}</a>.</li>
        <li>Recopiez ci-dessous la référence (ID) de la transaction indiquée dans le reçu {METHODS[method]}.</li>
      </ol>
      <div className="space-y-1.5"><Label htmlFor="payment-reference">Référence de la transaction</Label>
        <Input id="payment-reference" data-testid="payment-reference-input" required minLength={4} maxLength={60} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Ex : T_ABC123XYZ" className="bg-white" /></div>
      <p data-testid="manual-verification-notice" className="flex items-start gap-2 rounded-lg bg-white p-3 font-semibold text-green-900"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />Vérification manuelle avant activation : l'administratrice vérifie votre paiement, puis active elle-même votre abonnement.</p>
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} className="rounded-full" data-testid="payment-submit">J'ai payé, envoyer ma demande</Button>
        <Button type="button" variant="ghost" className="rounded-full" onClick={onCancel} data-testid="payment-cancel">Annuler</Button>
      </div>
    </form>
  );
};
